import { axiosInstance } from "@/utils/lib/axios";
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Paginated } from "./types";
import { generateParams } from "@/utils/helper/generateParams";
import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { swalErrorDefault } from "@/utils/lib/swal";

type NotificationQueryKey = DataConfig;

export interface NotificationData {
  id: string;
  notification: {
    id: string;
    createdDate: string;
    updatedDate: string | null;
    createdBy: string;
    updatedBy: string | null;
    deletedBy: string | null;
    deletedDate: string | null;
    type: "NOTIFICATION" | "TODO";
    module: string;
    statusData: string;
    message: string;
    targetRole: string;
    actionUrl: string | null;
  };
  isRead: boolean;
}

export interface NotificationList extends Paginated {
  content: NotificationData[];
}

/* ================ *
 * Read All Notif *
 * ================ */

async function getNotifications(
  queryFunctionContext: QueryFunctionContext,
): Promise<NotificationList> {
  const { queryKey } = queryFunctionContext;
  const [_, notificationQueryKey] = queryKey;
  const params = generateParams(notificationQueryKey as NotificationQueryKey);

  const repsonse = await axiosInstance.get("/api/crud/notification" + params);
  return repsonse.data.data;
}

function useNotifications(notificationQueryKey: NotificationQueryKey) {
  return useQuery({
    queryKey: ["notifications", notificationQueryKey],
    queryFn: getNotifications,
  });
}

/* ================ *
 * Counter Notif *
 * ================ */

async function counterNotification(): Promise<number> {
  const repsonse = await axiosInstance.get(
    "/api/crud/notification/unread-count",
  );
  return repsonse.data.data;
}

function useCounterNotification() {
  return useQuery({
    queryKey: ["notifications-counter"],
    queryFn: () => counterNotification(),
  });
}

/* ================ *
 * Counter Notif *
 * ================ */

async function getNotificationSummary(): Promise<{
  unreadTodo: number;
  unreadNotification: number;
  readCount: number;
}> {
  const repsonse = await axiosInstance.get("/api/crud/notification/summary");
  return repsonse.data.data;
}

function useNotificationSummary() {
  return useQuery({
    queryKey: ["notifications-summary"],
    queryFn: () => getNotificationSummary(),
  });
}

/* ================ *
 * User Notif Preference *
 * ================ */

type NotificationPreference = {
  id: string;
  isDoNotDisturb: boolean;
  isEmailEnabled: boolean;
  user: {
    id: string;
  };
};

async function getUserNotificationPreference(
  queryFunctionContext: QueryFunctionContext,
): Promise<NotificationPreference | null> {
  const { queryKey } = queryFunctionContext;
  const [_, { userId }] = queryKey as [string, { userId: string }];
  const params = generateParams({ userId });

  const repsonse = await axiosInstance.get(
    "/api/crud/user-notification-preference" + params,
  );

  return repsonse.data.data.content?.[0] ?? null;
}

function useUserNotificationPreference({ userId }: { userId: string }) {
  return useQuery({
    queryKey: ["user-notification-preference", { userId }],
    queryFn: getUserNotificationPreference,
  });
}

/* ==================== *
 * Read Notif *
 * ==================== */

async function readNotification(id: string) {
  const response = await axiosInstance.post(
    `/api/crud/notification/${id}/read`,
    {},
  );
  return response.data;
}

function useReadNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => readNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["notifications-counter"],
      });
      queryClient.invalidateQueries({
        queryKey: ["notifications-summary"],
      });
    },
  });
}

/* ==================== *
 * Unread Notif *
 * ==================== */

async function unreadNotification(id: string) {
  const response = await axiosInstance.post(
    `/api/crud/notification/${id}/unread`,
    {},
  );
  return response.data;
}

function useUnreadNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => unreadNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["notifications-counter"],
      });
      queryClient.invalidateQueries({
        queryKey: ["notifications-summary"],
      });
    },
  });
}

/* ==================== *
 * Read All Notif *
 * ==================== */

async function readAllNotification() {
  const response = await axiosInstance.post(
    `/api/crud/notification/read-all`,
    {},
  );
  return response.data;
}

function useReadAllNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => readAllNotification(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["notifications-counter"],
      });
      queryClient.invalidateQueries({
        queryKey: ["notifications-summary"],
      });
    },
    onError: () => {
      swalErrorDefault.fire({ text: "Failed to Read All Notification" });
    },
  });
}

/* ==================== *
 * Unread All Notif *
 * ==================== */

async function unreadAlllNotification() {
  const response = await axiosInstance.post(
    `/api/crud/notification/unread-all`,
    {},
  );
  return response.data;
}

function useUnreadAllNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unreadAlllNotification(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["notifications-counter"],
      });
      queryClient.invalidateQueries({
        queryKey: ["notifications-summary"],
      });
    },
    onError: () => {
      swalErrorDefault.fire({ text: "Failed to Unread All Notification" });
    },
  });
}

/* ==================== *
 * Update Notif Preference *
 * ==================== */

interface UpdateUserPreferencePayload {
  id: string;
  isDoNotDisturb: boolean;
  isEmailEnabled: boolean;
  user: {
    id: string;
  };
}

async function updateUserNotificationPreference(
  data: UpdateUserPreferencePayload,
) {
  const response = await axiosInstance.post(
    `/api/crud/user-notification-preference/${data.id}`,
    {
      isDoNotDisturb: data.isDoNotDisturb,
      isEmailEnabled: data.isEmailEnabled,
      user: {
        id: data.user.id,
      },
    },
  );
  return response.data;
}

function useUpdateUserNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateUserPreferencePayload) =>
      updateUserNotificationPreference(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user-notification-preference"],
      });
    },
    onError: () => {
      swalErrorDefault.fire({
        text: "failed to update user notification preference",
      });
    },
  });
}

/* ==================== *
 * Create Notif Preference *
 * ==================== */

interface CreateUserPreferencePayload {
  isDoNotDisturb: boolean;
  isEmailEnabled: boolean;
  user: {
    id: string;
  };
}

async function createUserNotificationPreference(
  data: CreateUserPreferencePayload,
) {
  const response = await axiosInstance.post(
    `/api/crud/user-notification-preference`,
    data,
  );

  return response.data;
}

function useCreateUserNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserPreferencePayload) =>
      createUserNotificationPreference(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user-notification-preference"],
      });
    },
    onError: () => {
      swalErrorDefault.fire({
        text: "failed to create user notification preference",
      });
    },
  });
}

export {
  useNotifications,
  useReadNotification,
  useUnreadNotification,
  useReadAllNotification,
  useUnreadAllNotifications,
  useCounterNotification,
  useUserNotificationPreference,
  useUpdateUserNotificationPreference,
  useCreateUserNotificationPreference,
  useNotificationSummary,
};
